import { Table, TableProps } from 'antd';
import { ParsedQs } from 'qs';
import { isEmpty } from 'ramda';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  Transaction,
  TransactionStatus,
} from 'utils/apiClient/services/shorepay/types';
import StatusTag, { statusDisplayText } from './TransactionStatusTag';

const statuses: TransactionStatus[] = [
  'failed',
  'canceled',
  'succeeded',
  'in_progress',
];

interface Props extends TableProps<Transaction> {
  filterLocal?: boolean;
  showReason?: boolean;
  activeFilters?: ParsedQs;
}

const TransactionsTable: React.FC<Props> = ({
  dataSource,
  loading,
  showReason = false,
  filterLocal = false,
  activeFilters = {},
  ...props
}) => {
  const intl = useIntl();

  return (
    <Table<Transaction>
      rowKey={(t) => t.created_at}
      dataSource={dataSource}
      loading={loading}
      size="middle"
      css={`
        .ant-table,
        .ant-table-thead > tr > th {
          color: rgba(0, 0, 0, 0.65);
          &:before {
            position: unset !important;
          }
        }
        .ant-table-thead > tr > th:first-child,
        .ant-table-tbody > tr > td:first-child {
          padding-left: 24px;
        }
        .ant-table-thead > tr > th:last-child,
        .ant-table-tbody > tr > td:last-child {
          padding-right: 24px;
        }
      `}
      {...props}
    >
      <Table.Column<Transaction>
        key="date"
        width="25%"
        title={intl.formatMessage({ defaultMessage: 'Date' })}
        render={(t: Transaction) =>
          `${intl.formatDate(t.created_at)} ${intl.formatTime(t.created_at)}`
        }
      />
      <Table.Column<Transaction>
        key="amount"
        width="10%"
        title={intl.formatMessage({ defaultMessage: 'Amount' })}
        render={(t: Transaction) => (
          <span css="font-weight: 500">
            {intl.formatNumber(parseFloat(t.amount), {
              currency: t.currency,
              style: 'currency',
            })}
          </span>
        )}
      />
      <Table.Column<Transaction>
        key="status"
        width="15%"
        title={intl.formatMessage({ defaultMessage: 'Status' })}
        render={(t: Transaction) => (
          <StatusTag
            status={isEmpty(t?.refund_set ?? []) ? t.status : 'refunded'}
          />
        )}
        filters={statuses.map((s) => ({
          value: s,
          text: statusDisplayText[s]
            ? intl.formatMessage(statusDisplayText[s])
            : '',
        }))}
        filterMultiple={false}
        filtered={!filterLocal && Boolean(activeFilters.status)}
        filteredValue={
          filterLocal
            ? undefined
            : [
                typeof activeFilters.status === 'string'
                  ? activeFilters.status
                  : '',
              ].filter((v) => v)
        }
        onFilter={
          filterLocal ? (value, record) => record.status === value : undefined
        }
      />

      {showReason && (
        <Table.Column<Transaction>
          key="reason"
          width="50%"
          title={intl.formatMessage({ defaultMessage: 'Reason' })}
          render={(t: Transaction) => (
            <span css="text-align: center">{t.reason ?? '-'}</span>
          )}
        />
      )}

      <Table.Column<Transaction>
        key="invoice_number"
        width="20%"
        title={intl.formatMessage({ defaultMessage: 'Invoice Number' })}
        dataIndex="invoice_number"
      />
      <Table.Column<Transaction>
        key="customer_name"
        width="20%"
        title={intl.formatMessage({ defaultMessage: 'Customer' })}
        dataIndex="customer_name"
      />

      <Table.Column<Transaction>
        key="card_type"
        width="20%"
        title={intl.formatMessage({ defaultMessage: 'Funding' })}
        dataIndex="card_type"
      />

      <Table.Column<Transaction>
        key="fee"
        width="20%"
        title={intl.formatMessage({ defaultMessage: 'Fee' })}
        render={(t: Transaction) => (
          <span css="font-weight: 500">
            {intl.formatNumber(parseFloat(t.fee), {
              currency: t.currency,
              style: 'currency',
            })}
          </span>
        )}
      />
    </Table>
  );
};

export default TransactionsTable;
